import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { CircleAlert, CircleCheck, Loader2, ShieldAlert } from "@fg/icons";
import { Button } from "@/components/ui/button";
import { returnState } from "@/auth/returnTo";

/**
 * Public form page (/forms/:token): a form published from Workbook, answered here. Outside ProtectedRoute, so a link set to
 * "anyone" opens with no sign-in; a link set to signed-in people or a list asks the visitor to sign in first.
 */
interface Question { title: string; type: string; options?: string | null; required: boolean }
interface PublicForm { title: string; description: string | null; questions: Question[]; accessMode: string; signedInAs: string | null; oneResponse: boolean; confirmation: string | null }
type Page =
    | { kind: "loading" }
    | { kind: "form"; form: PublicForm }
    | { kind: "signin"; title: string; message: string }
    | { kind: "denied"; title: string; message: string; as: string | null }
    | { kind: "closed"; title: string; message: string }
    | { kind: "missing" }
    | { kind: "offline" }
    | { kind: "done"; form: PublicForm; message: string };

const baseUrl = ((import.meta.env as Record<string, string | undefined>).VITE_DRIVE_BFF_BASE_URL ?? "").replace(/\/+$/, "");
const enc = encodeURIComponent;

/** The same rules the server applies (it checks again). */
function check(q: Question, raw: string): string | null {
    const v = raw.trim();
    if (!v) return q.required ? "This question is required." : null;
    switch (q.type) {
        case "Number": return Number.isFinite(Number(v)) ? null : "Enter a number.";
        case "Whole number": return /^-?\d+$/.test(v) ? null : "Enter a whole number.";
        case "Date": return /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v)) ? null : "Enter a date.";
        case "Email": return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Enter an email address.";
        case "Multiple choice": case "Dropdown": return (q.options ?? "").split(",").map((x) => x.trim()).includes(v) ? null : "Pick one of the options.";
        case "Checkbox (Yes/No)": return v === "Yes" || v === "No" ? null : "Choose Yes or No.";
        default: return null;
    }
}

const choices = (q: Question) => (q.options ?? "").split(",").map((x) => x.trim()).filter(Boolean);

export function FormPage() {
    const { token } = useParams<{ token: string }>();
    const auth = useAuth();
    const access = auth.user?.access_token;
    const [page, setPage] = useState<Page>({ kind: "loading" });
    const [answers, setAnswers] = useState<string[]>([]);
    const [errors, setErrors] = useState<Array<string | null>>([]);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    useEffect(() => {
        if (auth.isLoading || !token) return;
        let alive = true;
        void (async () => {
            let res: Response;
            try {
                res = await fetch(`${baseUrl}/api/forms/public/${enc(token)}`, { headers: { Accept: "application/json", ...(access ? { Authorization: `Bearer ${access}` } : {}) }, credentials: "omit" });
            } catch { if (alive) setPage({ kind: "offline" }); return; }
            const body = (await res.json().catch(() => ({}))) as Partial<PublicForm> & { title?: string; error?: string; signedInAs?: string };
            if (!alive) return;
            if (res.ok) { const f = body as PublicForm; setPage({ kind: "form", form: f }); setAnswers(f.questions.map(() => "")); document.title = f.title; }
            else if (res.status === 401) setPage({ kind: "signin", title: body.title ?? "Form", message: body.error ?? "Sign in to respond to this form." });
            else if (res.status === 403) setPage({ kind: "denied", title: body.title ?? "Form", message: body.error ?? "You cannot respond to this form.", as: body.signedInAs ?? null });
            else if (res.status === 410) setPage({ kind: "closed", title: body.title ?? "Form", message: body.error ?? "This form is closed." });
            else setPage({ kind: "missing" });
        })();
        return () => { alive = false; };
    }, [auth.isLoading, token, access]);

    const submit = async (form: PublicForm) => {
        const errs = form.questions.map((q, i) => check(q, answers[i] ?? ""));
        setErrors(errs);
        setProblem(null);
        if (errs.some(Boolean)) return;
        setBusy(true);
        try {
            const res = await fetch(`${baseUrl}/api/forms/public/${enc(token!)}/responses`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json", ...(access ? { Authorization: `Bearer ${access}` } : {}) },
                credentials: "omit",
                body: JSON.stringify({ answers: answers.map((a) => a.trim()) })
            });
            const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
            if (res.ok) setPage({ kind: "done", form, message: body.message ?? "Your response has been recorded." });
            else setProblem(body.error ?? `Could not send the response (${res.status}).`);
        } catch { setProblem("The form could not be sent. Check your connection and try again."); }
        finally { setBusy(false); }
    };

    const set = (i: number, v: string) => setAnswers((a) => a.map((x, k) => (k === i ? v : x)));
    const input = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";
    const shell = (children: React.ReactNode) => (
        <div className="min-h-screen bg-muted/40 px-4 py-8">
            <div className="mx-auto max-w-xl space-y-4">{children}</div>
        </div>
    );
    const card = "rounded-xl border bg-card p-6 shadow-sm";

    if (page.kind === "loading") return shell(<div className={`${card} flex items-center gap-2 text-sm text-muted-foreground`}><Loader2 className="h-4 w-4 animate-spin" /> Opening the form…</div>);
    if (page.kind === "missing") return shell(<div className={card}><h1 className="text-lg font-semibold">Form not found</h1><p className="mt-1 text-sm text-muted-foreground">This link is not valid. Ask the person who shared it for a new one.</p></div>);
    if (page.kind === "offline") return shell(<div className={card}><h1 className="text-lg font-semibold">Can’t reach the server</h1><p className="mt-1 text-sm text-muted-foreground">Check your connection and reload.</p></div>);
    if (page.kind === "closed") return shell(<div className={card}><h1 className="text-lg font-semibold">{page.title}</h1><p className="mt-2 flex items-center gap-2 text-sm"><CircleAlert className="h-4 w-4" /> {page.message}</p></div>);
    if (page.kind === "signin") {
        return shell(
            <div className={card}>
                <h1 className="text-lg font-semibold">{page.title}</h1>
                <p className="mt-2 flex items-center gap-2 text-sm"><ShieldAlert className="h-4 w-4" /> {page.message}</p>
                <Button className="mt-4" data-form-signin onClick={() => void auth.signinRedirect({ state: returnState() })}>Sign in</Button>
            </div>
        );
    }
    if (page.kind === "denied") {
        return shell(
            <div className={card}>
                <h1 className="text-lg font-semibold">{page.title}</h1>
                <p className="mt-2 flex items-center gap-2 text-sm"><ShieldAlert className="h-4 w-4" /> {page.message}</p>
                {page.as ? <p className="mt-1 text-xs text-muted-foreground">Signed in as {page.as}. This form is limited to certain people or domains.</p> : null}
                <Button variant="outline" className="mt-4" onClick={() => void auth.signoutRedirect({ state: returnState() })}>Use another account</Button>
            </div>
        );
    }
    if (page.kind === "done") {
        return shell(
            <div className={card} data-form-done>
                <h1 className="text-lg font-semibold">{page.form.title}</h1>
                <p className="mt-2 flex items-center gap-2 text-sm"><CircleCheck className="h-4 w-4 text-green-600" /> {page.message}</p>
                {!page.form.oneResponse ? <Button variant="outline" className="mt-4" onClick={() => { setAnswers(page.form.questions.map(() => "")); setErrors([]); setPage({ kind: "form", form: page.form }); }}>Submit another response</Button> : null}
            </div>
        );
    }

    const form = page.form;
    return shell(
        <>
            <div className={card}>
                <h1 className="text-xl font-semibold" data-form-title>{form.title}</h1>
                {form.description ? <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{form.description}</p> : null}
                {form.signedInAs ? <p className="mt-3 text-xs text-muted-foreground">Responding as {form.signedInAs}. Your email is recorded with your answers.</p> : null}
                <p className="mt-3 text-xs text-red-600">* Required</p>
            </div>
            {form.questions.map((q, i) => (
                <div key={i} className={card} data-question={i}>
                    <label className="block text-sm font-medium" htmlFor={`q${i}`}>{q.title}{q.required ? <span className="text-red-600"> *</span> : null}</label>
                    <div className="mt-2">
                        {q.type === "Paragraph" ? <textarea id={`q${i}`} rows={4} className={`${input} h-auto py-2`} value={answers[i] ?? ""} onChange={(e) => set(i, e.target.value)} />
                            : q.type === "Multiple choice" ? <div role="radiogroup" className="space-y-1.5">{choices(q).map((o) => <label key={o} className="flex items-center gap-2 text-sm"><input type="radio" name={`q${i}`} checked={answers[i] === o} onChange={() => set(i, o)} /> {o}</label>)}</div>
                            : q.type === "Dropdown" || q.type === "Checkbox (Yes/No)" ? (
                                <select id={`q${i}`} className={input} value={answers[i] ?? ""} onChange={(e) => set(i, e.target.value)}>
                                    <option value="">Choose</option>
                                    {(q.type === "Dropdown" ? choices(q) : ["Yes", "No"]).map((o) => <option key={o}>{o}</option>)}
                                </select>
                            ) : <input id={`q${i}`} type={q.type === "Date" ? "date" : q.type === "Email" ? "email" : "text"} inputMode={q.type === "Number" || q.type === "Whole number" ? "decimal" : undefined} className={input} value={answers[i] ?? ""} onChange={(e) => set(i, e.target.value)} />}
                    </div>
                    {errors[i] ? <p role="alert" className="mt-2 text-xs text-red-600">{errors[i]}</p> : null}
                </div>
            ))}
            {problem ? <p role="alert" className="text-sm text-red-600">{problem}</p> : null}
            <Button data-form-submit disabled={busy} onClick={() => void submit(form)}>{busy ? "Sending…" : "Submit"}</Button>
        </>
    );
}
